import { useEffect, useMemo } from 'react';
import { addError } from '@shared/utils/logger';
import { DEFAULT_CURRENCY } from './constants';
import { useCurrencyName } from './useCurrencyName';
import type { GetStationeryCurrenciesQuery } from './stationeryCurrencies.graphql.generated';
import { useGetStationeryCurrenciesQuery } from './stationeryCurrencies.graphql.generated';

/**
 * A currency as card_service sends it (`currencyCode` is not checked against a client-side list: the backend
 * decides what is sold), plus its translated display name.
 */
export type StationeryCurrency = GetStationeryCurrenciesQuery['currencyConversions'][number] & Readonly<{ name: string }>;

/** What a failed request falls back to: USD, shipping to the US — the pre-local-currency behavior. */
const FALLBACK_ROWS: GetStationeryCurrenciesQuery['currencyConversions'] = [{ __typename: 'CurrencyConversion', currencyCode: DEFAULT_CURRENCY, countryCodes: ['US'] }];

// Every caller mounts its own query observer; report each failure once, not once per caller.
const reportedErrors = new WeakSet<Error>();

/**
 * Every currency stationery and Joy Feature checkouts can be sold in, with the countries sold to in each.
 *
 * card_service's `currencyConversionsTable` is the source of truth: a currency in it is active, and its
 * `countryCodes` are the countries Joy sells to. Adding a market is a card_service change only.
 *
 * Returns `undefined` until the query resolves. Apollo caches it, so every caller shares one request. If the
 * request fails, the error is reported and the list is USD / US only, so a failure can't pass for an endless load.
 */
export const useStationeryCurrencies = (): ReadonlyArray<StationeryCurrency> | undefined => {
  const { data, error } = useGetStationeryCurrenciesQuery({ batchMode: 'fast' });
  const getName = useCurrencyName();

  useEffect(() => {
    if (!error || reportedErrors.has(error)) return;
    reportedErrors.add(error);
    addError(error, { context: 'useStationeryCurrencies' });
  }, [error]);

  const rows = data?.currencyConversions ?? (error ? FALLBACK_ROWS : undefined);

  return useMemo(() => rows?.map(row => ({ ...row, name: getName(row.currencyCode) })), [rows, getName]);
};
